'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';

const COOKIE_CONSENT_KEY = 'acv_cookie_consent';
const COOKIE_POLICY_VERSION = '1.0';

interface CookiePreferences {
  necessary: boolean;
  preferences: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface ConsentRecord {
  version: string;
  timestamp: string;
  preferences: CookiePreferences;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
};

function loadConsent(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ConsentRecord;
  } catch {
    return null;
  }
}

function saveConsent(preferences: CookiePreferences): void {
  const record: ConsentRecord = {
    version: COOKIE_POLICY_VERSION,
    timestamp: new Date().toISOString(),
    preferences,
  };
  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(record));
  // Notify AnalyticsScripts in the same tab
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('acv_consent_updated'));
  }
}

export function useCookieConsent() {
  const [consent, setConsent] = useState<ConsentRecord | null>(null);
  useEffect(() => {
    setConsent(loadConsent());
  }, []);
  return consent;
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>(DEFAULT_PREFERENCES);

  useEffect(() => {
    const existing = loadConsent();
    if (!existing) {
      setVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const all: CookiePreferences = { necessary: true, preferences: true, analytics: true, marketing: true };
    saveConsent(all);
    setVisible(false);
  };

  const handleRejectAll = () => {
    saveConsent(DEFAULT_PREFERENCES);
    setVisible(false);
  };

  const handleSaveConfig = () => {
    saveConsent({ ...prefs, necessary: true });
    setVisible(false);
    setConfiguring(false);
  };

  const togglePref = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return;
    setPrefs(p => ({ ...p, [key]: !p[key] }));
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Configuración de cookies"
      className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)', pointerEvents: 'none' }}
    >
      <div
        className="max-w-2xl mx-auto rounded-2xl p-5 shadow-2xl"
        style={{
          background: 'hsl(216, 22%, 11%)',
          border: '1px solid hsl(216, 15%, 22%)',
          pointerEvents: 'all',
        }}
      >
        {!configuring ? (
          <>
            <p className="text-sm mb-4" style={{ color: 'hsl(216, 15%, 75%)' }}>
              Usamos cookies propias necesarias para el funcionamiento del servicio y, con tu consentimiento, cookies de análisis de la plataforma.{' '}
              <Link href="/cookies" className="underline hover:opacity-80" style={{ color: 'hsl(248, 80%, 70%)' }}>
                Política de cookies
              </Link>
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleAcceptAll}
                className="flex-1 min-w-[120px] px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #7c6ff7, #4f46e5)', color: 'white' }}
              >
                Aceptar todas
              </button>
              <button
                onClick={handleRejectAll}
                className="flex-1 min-w-[120px] px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                style={{ background: 'hsl(216, 15%, 20%)', border: '1px solid hsl(216, 15%, 30%)', color: 'hsl(216, 15%, 85%)' }}
              >
                Rechazar todas
              </button>
              <button
                onClick={() => setConfiguring(true)}
                className="flex-1 min-w-[120px] px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-80"
                style={{ color: 'hsl(248, 80%, 70%)', border: '1px solid hsl(248, 40%, 35%)' }}
              >
                Configurar
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-sm font-bold mb-3" style={{ color: 'hsl(216, 15%, 92%)' }}>
              Configurar preferencias de cookies
            </h2>
            <div className="space-y-3 mb-4">
              {(
                [
                  { key: 'necessary', label: 'Necesarias', desc: 'Imprescindibles para el funcionamiento del servicio (sesión, autenticación). No pueden desactivarse.' },
                  { key: 'preferences', label: 'Preferencias', desc: 'Recuerdan tus ajustes de idioma, tema u otras preferencias de uso.' },
                  { key: 'analytics', label: 'Analíticas', desc: 'Permiten medir el uso de la plataforma para mejorar el servicio (Rocket Analytics).' },
                  { key: 'marketing', label: 'Marketing', desc: 'Utilizadas para mostrar publicidad relevante. Actualmente no se usan.' },
                ] as { key: keyof CookiePreferences; label: string; desc: string }[]
              ).map(({ key, label, desc }) => (
                <div key={key} className="flex items-start gap-3 rounded-lg p-3" style={{ background: 'hsl(216, 22%, 14%)' }}>
                  <div className="flex-1">
                    <div className="text-xs font-semibold mb-0.5" style={{ color: 'hsl(216, 15%, 88%)' }}>{label}</div>
                    <div className="text-xs" style={{ color: 'hsl(216, 15%, 55%)' }}>{desc}</div>
                  </div>
                  <button
                    role="switch"
                    aria-checked={prefs[key]}
                    aria-label={`${label} ${prefs[key] ? 'activadas' : 'desactivadas'}`}
                    onClick={() => togglePref(key)}
                    disabled={key === 'necessary'}
                    className="relative shrink-0 w-10 h-5 rounded-full transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed mt-0.5"
                    style={{
                      background: prefs[key] ? '#7c6ff7' : 'hsl(216, 15%, 28%)',
                    }}
                  >
                    <span
                      className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform duration-200"
                      style={{ transform: prefs[key] ? 'translateX(20px)' : 'translateX(0)' }}
                    />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSaveConfig}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #7c6ff7, #4f46e5)', color: 'white' }}
              >
                Guardar preferencias
              </button>
              <button
                onClick={() => setConfiguring(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-80"
                style={{ color: 'hsl(216, 15%, 60%)', border: '1px solid hsl(216, 15%, 25%)' }}
              >
                Volver
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function CookieSettingsLink() {
  const [, setShow] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    window.location.reload();
  };

  return (
    <button
      onClick={handleClick}
      className="text-xs transition-colors hover:opacity-80"
      style={{ color: 'rgba(255,255,255,0.35)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
    >
      Configurar cookies
    </button>
  );
}
